// src/features/users/UserWorkspacePage.tsx
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { Server, ShoppingCart, ShieldCheck, WalletCards } from 'lucide-react';

import { formatPersianNumber, LoadingBoundary, PageContainer, PageProgress } from '../../app/components/foundation';
import { ControlPlaneMetricStrip } from '../../app/components/foundation/control-plane';
import { creditUserWallet, debitUserWallet, updateUser } from './api/users.api';
import { getTenants } from '../tenants/api/tenants.api';
import {
  UserWorkspaceHeader,
  UserInfoCard,
  UserTenantCard,
  UserServicesCard,
  UserWalletCard,
  UserOrdersCard,
  WalletAdjustmentDialog,
  UserEditDialog,
} from './components';
import { useUserWorkspace } from './hooks/useUserWorkspace';
import './styles/users.css';

/**
 * User Workspace Foundation (/users/:id). Same shell pattern as
 * ServiceWorkspacePage/CommissionWorkspacePage: PageContainer +
 * feature-owned header, card grid below, RTL/Persian throughout,
 * existing tokens/primitives only.
 *
 * Reads `id` from the route params and resolves the user through the
 * authenticated Super Admin users API.
 */
export function UserWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const {
    user, tenant, services, wallet, orders, ordersPage, orderPage, setOrderPage, refetch,
    ordersLoading, ordersError, loading, error,
  } = useUserWorkspace(id);
  const [walletDirection, setWalletDirection] = useState<'credit' | 'debit' | null>(null);
  const [walletMutationLoading, setWalletMutationLoading] = useState(false);
  const [walletMutationError, setWalletMutationError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [tenantOptions, setTenantOptions] = useState<Array<{ id: number; name: string }>>([]);
  if (loading && !user) {
    return <PageContainer className="flex flex-col gap-6"><LoadingBoundary loading /></PageContainer>;
  }

  if (error) {
    return <PageContainer className="flex flex-col gap-6"><LoadingBoundary loading={false} error={error} /></PageContainer>;
  }

  if (!user) {
    return (
      <PageContainer className="flex flex-col gap-6">
        <p className="vania-user-workspace-empty">کاربر مورد نظر یافت نشد.</p>
      </PageContainer>
    );
  }

  async function handleWalletAdjustment(payload: { amount: number; reason: string }) {
    if (!id || !walletDirection) return;
    setWalletMutationLoading(true);
    setWalletMutationError(null);
    try {
      if (walletDirection === 'credit') await creditUserWallet(id, payload);
      else await debitUserWallet(id, payload);
      setWalletDirection(null);
      refetch();
    } catch (requestError) {
      const record = requestError && typeof requestError === 'object' ? requestError as Record<string, unknown> : {};
      const body = record.body && typeof record.body === 'object' ? record.body as Record<string, unknown> : {};
      const detail = typeof body.detail === 'string' ? body.detail : '';
      setWalletMutationError(detail === 'INSUFFICIENT_BALANCE' ? 'موجودی برای این کاهش کافی نیست.' : 'تغییر موجودی انجام نشد.');
    } finally {
      setWalletMutationLoading(false);
    }
  }

  async function handleUserUpdate(payload: Parameters<typeof updateUser>[1]) {
    if (!id) return;
    setEditSubmitting(true);
    setEditError(null);
    try {
      await updateUser(id, payload);
      setEditOpen(false);
      await refetch();
    } catch (requestError) {
      const record = requestError && typeof requestError === 'object' ? requestError as Record<string, unknown> : {};
      const body = record.body && typeof record.body === 'object' ? record.body as Record<string, unknown> : {};
      const detail = typeof body.detail === 'string' ? body.detail : '';
      const messages: Record<string, string> = {
        USERNAME_EXISTS: 'این نام کاربری قبلاً ثبت شده است.',
        PHONE_EXISTS: 'این شماره موبایل قبلاً ثبت شده است.',
        TENANT_NOT_FOUND: 'تننت انتخاب‌شده معتبر نیست.',
      };
      setEditError(messages[detail] || 'ویرایش کاربر انجام نشد.');
    } finally {
      setEditSubmitting(false);
    }
  }

  async function handleToggleBlocked() {
    if (!id || !user || editSubmitting) return;
    const nextBlocked = user.status === 'active';
    if (!window.confirm(nextBlocked ? 'آیا از مسدود کردن این کاربر مطمئن هستید؟' : 'آیا از رفع مسدودی این کاربر مطمئن هستید؟')) return;
    await handleUserUpdate({ is_blocked: nextBlocked });
  }

  return (
    <>
      <PageContainer className="vania-user-profile-page">
      <PageProgress active={loading} />
      <UserWorkspaceHeader
        user={user}
        actionLoading={editSubmitting}
        onEdit={() => {
          setEditError(null);
          setEditOpen(true);
          getTenants(1, 50)
            .then((data) => setTenantOptions(data.tenants.map((item) => ({ id: item.id, name: item.name }))))
            .catch(() => setEditError('خطا در دریافت فهرست تننت‌ها'));
        }}
        onToggleBlocked={handleToggleBlocked}
      />
      <ControlPlaneMetricStrip
        className="vania-user-profile-summary"
        items={[
          {
            label: 'وضعیت حساب',
            value: user.status === 'active' ? 'فعال' : 'تعلیق‌شده',
            icon: <ShieldCheck size={16} />,
            tone: user.status === 'active' ? 'success' : 'danger',
          },
          {
            label: 'سرویس‌ها',
            value: services === undefined ? '—' : formatPersianNumber(services.length),
            icon: <Server size={16} />,
            tone: 'info',
          },
          {
            label: 'سفارش‌ها',
            value: formatPersianNumber(ordersPage?.totalCount ?? orders?.totalCount ?? 0),
            icon: <ShoppingCart size={16} />,
            tone: 'primary',
          },
          {
            label: 'موجودی کیف پول',
            value: wallet === undefined ? '—' : `${formatPersianNumber(wallet.balance)} تومان`,
            icon: <WalletCards size={16} />,
            tone: 'success',
          },
        ]}
      />

      <div className="vania-user-workspace-grid">
        <UserInfoCard user={user} />
        <UserTenantCard tenant={tenant} />
        <UserServicesCard services={services} />
        <UserOrdersCard
          orders={ordersPage}
          loading={ordersLoading}
          error={ordersError}
          onPageChange={setOrderPage}
          currentPage={orderPage}
        />
          <UserWalletCard
            wallet={wallet}
            onCredit={() => { setWalletMutationError(null); setWalletDirection('credit'); }}
            onDebit={() => { setWalletMutationError(null); setWalletDirection('debit'); }}
            mutationLoading={walletMutationLoading}
            mutationError={walletMutationError}
          />
      </div>
      </PageContainer>
      <WalletAdjustmentDialog
        open={walletDirection !== null}
        direction={walletDirection ?? 'credit'}
        submitting={walletMutationLoading}
        error={walletMutationError}
        onClose={() => { if (!walletMutationLoading) setWalletDirection(null); }}
        onSubmit={handleWalletAdjustment}
      />
      {user ? (
        <UserEditDialog
          open={editOpen}
          user={user}
          tenants={tenantOptions}
          submitting={editSubmitting}
          error={editError}
          onClose={() => { if (!editSubmitting) setEditOpen(false); }}
          onSubmit={handleUserUpdate}
        />
      ) : null}
    </>
  );
}
